import { useHistory } from 'react-router-dom'
import moment from 'moment'
import { toUpper, keys, trim, isObject } from 'lodash'
import LoadingIcon from 'components/atoms/LoadingIcon'
import EmptyState from 'components/pageComponents/EmptyState'
import { formatPrice } from 'lib/utils'

const BookingInvoiceProfile = ({ readOnly, refetch, invoiceData, loading, id, isEdit }) => {
  const history = useHistory()

  const navigateToBookingDetail = bookingId => {
    history.push(`/booking/${bookingId}`)
  }
  const getStatusColor = status => {
    const statusColors = {
      pending: 'bg-yellow-100 text-yellow-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800',
      failed: 'bg-red-100 text-red-800',
    }
    return statusColors[status] || statusColors.pending
  }

  const renderUpdatedInfo = updatedInfo => {
    if (!updatedInfo || keys(updatedInfo).length === 0) {
      return (
        <div className="text-center py-6">
          <div className="mx-auto h-10 w-10 text-gray-400 mb-3">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <p className="text-gray-500 text-sm">No updates available</p>
        </div>
      )
    }

    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Update History</h3>
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {keys(updatedInfo).length} {keys(updatedInfo).length === 1 ? 'Update' : 'Updates'}
          </span>
        </div>

        {Object.entries(updatedInfo).map(([key, value], index) => (
          <div key={key} className="bg-gray-50 rounded-lg p-4 border-l-4 border-blue-500">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="inline-flex items-center justify-center w-5 h-5 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full">{index + 1}</span>
                  <span className="text-sm font-semibold text-gray-900 capitalize">{trim(key.replace(/([A-Z])/g, ' $1'))}</span>
                </div>

                <div className="ml-7">
                  {isObject(value) ? (
                    <div className="bg-white rounded-md p-3 border">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">JSON Data</span>
                        <button
                          onClick={() => navigator.clipboard.writeText(JSON.stringify(value, null, 2))}
                          className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                            />
                          </svg>
                          Copy
                        </button>
                      </div>
                      <pre className="text-xs text-gray-700 whitespace-pre-wrap overflow-x-auto bg-gray-50 p-2 rounded border">{JSON.stringify(value, null, 2)}</pre>
                    </div>
                  ) : (
                    <div className="bg-white rounded-md p-3 border">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Value</span>
                        <button onClick={() => navigator.clipboard.writeText(String(value))} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                            />
                          </svg>
                          Copy
                        </button>
                      </div>
                      <span className="text-sm text-gray-700 break-words block bg-gray-50 p-2 rounded border">{String(value)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (loading) {
    return (
      <div className="w-full h-[400px] flex justify-center items-center">
        <LoadingIcon />
      </div>
    )
  }

  if (!invoiceData) {
    return <EmptyState v2 description="Invoice not found" />
  }

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Invoice Information */}
        <div className="bg-white shadow-lg rounded-lg p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Invoice Information</h2>
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
              <span className="text-sm font-medium text-gray-600">Invoice ID:</span>
              <span className="text-sm text-gray-900 font-mono">{invoiceData._id}</span>
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
              <span className="text-sm font-medium text-gray-600">Invoice Status:</span>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(invoiceData.invoiceStatus)}`}>
                {toUpper(invoiceData.invoiceStatus?.charAt(0)) + invoiceData.invoiceStatus?.slice(1)}
              </span>
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
              <span className="text-sm font-medium text-gray-600">Payment Status:</span>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(invoiceData.invoicePaymentStatus)}`}>
                {toUpper(invoiceData.invoicePaymentStatus?.charAt(0)) + invoiceData.invoicePaymentStatus?.slice(1)}
              </span>
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
              <span className="text-sm font-medium text-gray-600">Payable Amount:</span>
              <span className="text-sm font-semibold text-gray-900">OMR {invoiceData.payableAmount ? formatPrice(invoiceData.payableAmount, true) : '0.000'}</span>
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
              <span className="text-sm font-medium text-gray-600">Created:</span>
              <span className="text-sm text-gray-900">{invoiceData.createdAt ? moment(invoiceData.createdAt).format('MMM DD, YYYY HH:mm') : 'N/A'}</span>
            </div>

            {invoiceData.reason && (
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium text-gray-600">Reason:</span>
                <span className="text-sm text-gray-900">{invoiceData.reason}</span>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white shadow-lg rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Booking Information</h2>
            {invoiceData.booking && (
              <button
                onClick={() => navigateToBookingDetail(invoiceData.booking._id)}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500 transition-colors duration-200"
              >
                View Booking Detail
              </button>
            )}
          </div>
          {invoiceData.booking ? (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                <span className="text-sm font-medium text-gray-600">Booking ID:</span>
                <span className="text-sm text-gray-900 font-mono">{invoiceData.booking._id}</span>
              </div>

              {invoiceData.booking.rentalCar && (
                <>
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                    <span className="text-sm font-medium text-gray-600">Car:</span>
                    <span className="text-sm text-gray-900">
                      {invoiceData.booking.rentalCar.car?.make?.make || ''} {invoiceData.booking.rentalCar.car?.model?.modal || ''} {invoiceData.booking.rentalCar.modelYear || ''}
                    </span>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                    <span className="text-sm font-medium text-gray-600">VIN:</span>
                    <span className="text-sm text-gray-900 font-mono">{invoiceData.booking.rentalCar.vin || 'N/A'}</span>
                  </div>
                </>
              )}

              {/* {invoiceData.booking.user && (
                <>
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                    <span className="text-sm font-medium text-gray-600">Customer:</span>
                    <span className="text-sm text-gray-900">{invoiceData.booking.user.fullName || 'N/A'}</span>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                    <span className="text-sm font-medium text-gray-600">Email:</span>
                    <span className="text-sm text-gray-900">{invoiceData.booking.user.email || 'N/A'}</span>
                  </div>
                </>
              )} */}

              <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                <span className="text-sm font-medium text-gray-600">Booking Status:</span>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(invoiceData.booking.status)}`}>
                  {toUpper(invoiceData.booking.status?.charAt(0)) + invoiceData.booking.status?.slice(1)}
                </span>
              </div>
              <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                <span className="text-sm font-medium text-gray-600">Payment Status:</span>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(invoiceData.booking.paymentStatus)}`}>
                  {toUpper(invoiceData.booking.paymentStatus?.charAt(0)) + invoiceData.booking.paymentStatus?.slice(1)}
                </span>
              </div>
            </div>
          ) : (
            <span className="text-gray-500">No booking information available</span>
          )}
        </div>

        {/* Updated Information */}
        <div className="bg-white shadow-lg rounded-lg p-6 lg:col-span-2">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Updated Information</h2>
          {renderUpdatedInfo(invoiceData.updatedInfo)}
        </div>
      </div>
    </div>
  )
}

export default BookingInvoiceProfile
